import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import AppShell from "./components/AppShell";
import ForcePasswordChange from "./components/ForcePasswordChange";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import Proposals from "./pages/Proposals";
import WorkOrders from "./pages/WorkOrders";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Invoices from "./pages/Invoices";
import Tasks from "./pages/Tasks";
import DAR from "./pages/DAR";
import MOM from "./pages/MOM";
import Compliance from "./pages/Compliance";
import Monitoring from "./pages/Monitoring";
import Team from "./pages/Team";
import Repository from "./pages/Repository";
import Settings from "./pages/Settings";
import Reports from "./pages/Reports";
import AuditLog from "./pages/AuditLog";
import ClientPortal from "./pages/ClientPortal";
import "./App.css";

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (allow && !allow.includes(user.role)) {
    return <Navigate to={user.role === "client" ? "/portal" : "/"} replace />;
  }
  return children;
}

function Shell({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/portal" replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal" element={<Protected allow={["client"]}><ClientPortal /></Protected>} />
      <Route path="/" element={<Protected><Shell><Dashboard /></Shell></Protected>} />
      <Route path="/leads" element={<Protected><Shell><Leads /></Shell></Protected>} />
      <Route path="/proposals" element={<Protected><Shell><Proposals /></Shell></Protected>} />
      <Route path="/work-orders" element={<Protected><Shell><WorkOrders /></Shell></Protected>} />
      <Route path="/projects" element={<Protected><Shell><Projects /></Shell></Protected>} />
      <Route path="/projects/:id" element={<Protected><Shell><ProjectDetail /></Shell></Protected>} />
      <Route path="/invoices" element={<Protected><Shell><Invoices /></Shell></Protected>} />
      <Route path="/tasks" element={<Protected><Shell><Tasks /></Shell></Protected>} />
      <Route path="/dar" element={<Protected><Shell><DAR /></Shell></Protected>} />
      <Route path="/mom" element={<Protected><Shell><MOM /></Shell></Protected>} />
      <Route path="/compliance" element={<Protected><Shell><Compliance /></Shell></Protected>} />
      <Route path="/monitoring" element={<Protected><Shell><Monitoring /></Shell></Protected>} />
      <Route path="/team" element={<Protected><Shell><Team /></Shell></Protected>} />
      <Route path="/repository" element={<Protected><Shell><Repository /></Shell></Protected>} />
      <Route path="/settings" element={<Protected allow={["super_admin"]}><Shell><Settings /></Shell></Protected>} />
      <Route path="/audit" element={<Protected allow={["super_admin", "hod", "senior_associate", "junior_associate"]}><Shell><AuditLog /></Shell></Protected>} />
      <Route path="/reports" element={<Protected allow={["super_admin", "hod", "senior_associate", "junior_associate"]}><Shell><Reports /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <ForcePasswordChange />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
